import React from 'react';

import { getFullName, isDefined } from '@nfctron/nexus-core';
import { UiButton, UiDropdownMenu, UiDropdownMenuContent, UiDropdownMenuItem, UiDropdownMenuTrigger } from '@nfctron/nexus-react';

import { AppShell, Avatar, Container, Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { useIsFetching } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronLeftIcon, LogInIcon, LogOutIcon, UserIcon } from 'lucide-react';
import Link from 'next/link';

import { activeSessionQueryKey } from '@root/config/src/features/authentication/hooks/useActiveSession';

import { useSessionStore } from '@root/features/authentication/stores/sessionStore';
import { type HeaderProps } from '@root/features/layout/components/Header/Header.types';
import { LanguageSelect } from '@root/features/layout/components/LanguageSelect/LanguageSelect';

import { AccountSheet } from '@root/lib/components/AccountSheet/AccountSheet';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { useLogout } from '../../../authentication/hooks/useLogout';

/**
 * Header component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Header: Component<HeaderProps> = ({ logoNode, backLink, size = 'xs' }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, Header.displayName);
	const session = useSessionStore((state) => state.account);
	const isAuthenticating = useIsFetching({ queryKey: activeSessionQueryKey }) > 0;

	const logoutMutation = useLogout();
	const [accountSheetOpen, setAccountSheetOpen] = React.useState(false);

	return (
		<React.Fragment>
			<AppShell.Header
				style={{ backdropFilter: 'blur(5px)' }}
				classNames={{
					header: 'sticky bg-white lg:bg-white/[97.5%] top-0 z-50 w-screen border-zinc-200',
				}}
			>
				<Container size="responsive" className="flex h-full max-w-screen-sm items-center gap-3 xl:max-w-screen-xl">
					{/* NFCtron compact logo */}
					{isDefined(backLink) ? (
						<Link locale={false} href={backLink} className="flex items-center gap-2 py-2">
							<ChevronLeftIcon size={22} />
							{logoNode}
						</Link>
					) : (
						logoNode
					)}

					<div className="ml-auto">
						{isDefined(session) ? (
							/** logged in menu */
							<UiDropdownMenu>
								<UiDropdownMenuTrigger asChild>
									<UiButton
										variant="ghost"
										className="rounded-full"
										rightSection={<ChevronDownIcon />}
										loading={isAuthenticating}
										disabled={isAuthenticating}
									>
										<Text fw={700} c="lucky-point" size="md">
											{getFullName(session.account)}
										</Text>
									</UiButton>
								</UiDropdownMenuTrigger>

								<UiDropdownMenuContent align="end" sideOffset={15}>
									<UiDropdownMenuItem className="cursor-default rounded-md">
										<div className="flex items-center justify-between gap-3 p-1.5">
											<div>
												<Text fw={300} c="lucky-point" size="md">
													{getFullName(session.account)}
												</Text>
												<Text className="text-sm" c="dimmed" fw={200}>
													{session.account.email}
												</Text>
											</div>

											<Avatar size={44} color="purple-heart" radius="full">
												<UserIcon size={16} />
											</Avatar>
										</div>
									</UiDropdownMenuItem>

									<div className="my-1 h-px bg-zinc-200" />

									<UiDropdownMenuItem
										className="cursor-pointer rounded-md px-3 py-[7.5px] text-base font-[400] text-[#FA4FB5] focus:bg-pink-50 focus:text-[#FA4FB5]"
										onClick={() => {
											openConfirmModal({
												title: t('logoutConfirmation.title'),
												centered: true,
												children: <Text size="md">{t('logoutConfirmation.text')}</Text>,
												labels: {
													confirm: t('logoutConfirmation.confirmButton.text'),
													cancel: t('logoutConfirmation.cancelButton.text'),
												},
												size: 500,
												confirmProps: { color: '#F9B44E', className: 'h-9 w-[117.68px]' },
												cancelProps: {
													color: 'zinc',
													variant: 'light',
													className: 'h-9 w-[153.84px] bg-[#FAFAFA] hover:bg-[#F8F8F8] text-[#71717A] hover:text-[#71717A]',
												},
												onConfirm: () => logoutMutation.mutate(),
											});
										}}
									>
										<LogOutIcon className="mr-3 size-4" />
										{t('logoutButton.text')}
									</UiDropdownMenuItem>
								</UiDropdownMenuContent>
							</UiDropdownMenu>
						) : (
							/** login button */
							<UiButton
								variant="primary"
								className="rounded-full"
								rightSection={<LogInIcon />}
								onClick={() => setAccountSheetOpen(true)}
								loading={isAuthenticating}
							>
								{t('loginButton.text')}
							</UiButton>
						)}
					</div>

					{/*	language toggle */}
					<LanguageSelect />

					{/* snow fall */}
					{/*<UiButton*/}
					{/*	variant="unstyled"*/}
					{/*	size="icon"*/}
					{/*	className={cn('rounded-full hover:bg-blue-100', visualFx.snowFallController.value ? 'bg-blue-50' : 'bg-zinc-100')}*/}
					{/*	onClick={visualFx.snowFallController.toggle}*/}
					{/*>*/}
					{/*	<SnowflakeIcon className={cn('size-5', visualFx.snowFallController.value ? 'text-blue-500!' : 'text-zinc-500')} />*/}
					{/*</UiButton>*/}
				</Container>
			</AppShell.Header>

			{/* account sheet */}
			<AccountSheet open={accountSheetOpen} onClose={() => setAccountSheetOpen(false)} useAs="login" type="base" />
		</React.Fragment>
	);
};

/**
 * Header component display name
 * @type {ComponentDisplayName}
 */
Header.displayName = 'component/Header';
